import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Params } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { FontTypeEnum } from './model/enums/FontTypeEnum';
import { ConfigSettingDto } from './model/business/ConfigSettingDto';
import { InsuredAttributesDto } from './model/business/calculation/InsuredAttributesDto';
import { CalculationRequestDto } from 'src/app/model/business/CalculationRequestDto';
import { CalculationResponseResultPremiumDto } from './model/business/calculation/CalculationResponseResultPremiumDto';
import { ProductTypeEnum } from './model/enums/ProductTypeEnum';
import { PolicyTerms } from './model/enums/PolicyTermsEnum';
import { LanguageEnum } from './model/enums/LanguageEnum';

const defaultCalculationResponseResult = {
  insurer: 'QUANTUM',
  premium: 0,
  policyTerms: PolicyTerms.QL_GG_2020_06,
  insured1Amount: 0,
  insured2Amount: 0,
};

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  getCalculationResponse: BehaviorSubject<CalculationResponseResultPremiumDto> =
    new BehaviorSubject(defaultCalculationResponseResult as CalculationResponseResultPremiumDto);

  private calculationResponse: CalculationResponseResultPremiumDto;
  private calculationRequest: CalculationRequestDto;
  private configSettingsDto = new ConfigSettingDto();
  private parsedLocale: string;
  private parsedIntermediaryId: string;
  private parsedProductType: string;

  constructor(
    @Inject('API_URL') private baseUrl: string,
    private httpService: HttpClient
  ) {
    this.configSettingsDto.fontSizeParagraph = 16.0;
    this.configSettingsDto.fontSizeH1 = 32.0;
    this.configSettingsDto.fontSizeH2 = 24.0;
    this.configSettingsDto.inputColor = 'rgb(0, 48, 86)';
    this.configSettingsDto.fontColor = 'rgb(0, 48, 86)';
    this.configSettingsDto.backgroundColor = 'rgb(229, 237, 244)';
    this.configSettingsDto.backgroundTextColor = '#FFFFFF';
    this.configSettingsDto.fontType = FontTypeEnum.Sun;
    this.configSettingsDto.inputBorderColor = 'rgb(0, 138, 201, 0.6)';
  }

  getLocale(): string {
    return this.parsedLocale;
  }

  getFirstInsured(): InsuredAttributesDto {
    return this.calculationRequest?.insureds[0];
  }

  getSecondInsured(): InsuredAttributesDto {
    if (!this.calculationRequest?.insureds[1]) {
      return null;
    }
    return this.calculationRequest?.insureds[1];
  }

  setCalculationResponse(calculationResponse): void {
    this.getCalculationResponse.next(calculationResponse);
  }

  resetCalculationResponse(): void {
    this.getCalculationResponse.next(defaultCalculationResponseResult as CalculationResponseResultPremiumDto);
  }

  resetCalculationRequest(): void {
    this.calculationRequest = undefined;
  }

  setCalculationRequest(calculationRequest: CalculationRequestDto): void {
    this.calculationRequest = calculationRequest;
  }

  getCallculationRequest(): CalculationRequestDto {
    if (!this.calculationRequest) {
      return null;
    }
    return this.calculationRequest;
  }

  getConfigSettingsDto(): ConfigSettingDto {
    return this.configSettingsDto;
  }

  getIntermediaryId(): string {
    return this.parsedIntermediaryId;
  }

  getProductType(): string {
    return this.parsedProductType;
  }

  loadConfigSettings(params: Params): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.parsedIntermediaryId = params.intermediaryId;
      this.parsedProductType = params.productType;
      this.parsedLocale = params.locale;

      if (
        this.parsedIntermediaryId === undefined ||
        this.parsedProductType === undefined
      ) {
        resolve(true);
        return;
      }

      this.httpService
        .get<ConfigSettingDto>(
          this.baseUrl + 'configSetting/getConfigSettings',
          {
            params: {
              intermediary_id: this.parsedIntermediaryId,
              productType: this.parsedProductType.toUpperCase(),
            },
          }
        )
        .subscribe(
          (result) => {
            if (result) {
              this.configSettingsDto = result;
            }
            resolve(true);
          },
          (error) => {
            // Ignore
            reject();
          }
        );
      });
  }

  get closingText(): string {
    return this.configSettingsDto.closingText;
  }

  get policyTerms(): string {
      return this.calculationResponse?.policyTerms;
  }

  get intermediaryName(): string {
    return this.configSettingsDto.intermediaryName;
  }

  get intermediaryEmail(): string{
    return this.configSettingsDto.intermediaryEmail;
  }

  get intermediaryPhone(): string{
    return this.configSettingsDto.intermediaryPhone;
  }

  get intermediaryFee(): string {
    return this.configSettingsDto.intermediaryFee;
  }

  get showIntermediaryFee(): boolean {
    return this.configSettingsDto.showIntermediaryFee;
  }

  get showIntermediarySubscriptionFee(): boolean {
    return this.configSettingsDto.showIntermediarySubscriptionFee;
  }

  get intermediarySubscriptionFee(): string {
    return this.configSettingsDto.intermediarySubscriptionFee;
  }

  get intermediarySubscriptionFeeText(): string {
    return this.configSettingsDto.intermediarySubscriptionFeeText;
  }

  get birthDate(): Date {
    const firstInsured = this.getFirstInsured();
    return firstInsured.dateOfBirth;
  }

  get insuredAmount(): number {
    const firstInsured = this.getFirstInsured();
    const covers = firstInsured.covers[0];
    return covers.sumAssured;
  }

  get product(): ProductTypeEnum {
    return this.calculationRequest.productType;
  }

  get termOfProduct(): number {
    return this.calculationRequest.numberOfYears;
  }

  get typeOfBenefit(): string {
    const firstInsured = this.getFirstInsured();
    const covers = firstInsured.covers[0];
    return covers.variables.typeOfCover.value;
  }

  get isSecondInsured(): boolean {
    return this.getSecondInsured() != null;
  }

  get secondInsuredBirthDate(): Date {
    if (!this.isSecondInsured) {
      return null;
    }
    const secondInsured = this.getSecondInsured();
    return secondInsured.dateOfBirth;
  }

  get secondInsuredInsuredAmount(): number {
    if (!this.isSecondInsured) {
      return null;
    }

    const secondInsured = this.getSecondInsured();
    const covers = secondInsured.covers[0];
    return covers.sumAssured;
  }

  get startDate(): Date {
    return this.calculationRequest.dateOfCommencement;
  }

  get endDate(): Date {
    const firstInsured = this.getFirstInsured();
    const covers = firstInsured.covers[0];
    return covers.endDate;
  }
}
